import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  Signal,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  NgbActiveModal,
  NgbModal,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { toObservable } from '@angular/core/rxjs-interop';
import { Bundle, Country } from '@core/core.types';
import { GetCountriesAction } from '@core/store/countries/countries.actions';
import { CountriesState } from '@core/store/countries/countries.state';
import { ProductsListState } from '@core/store/products/products.state';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { UserDisplayComponent } from '@shared/components/user-display/user-display.component';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import moment from 'moment';
import { PricingCountryUtilsService } from '../../../services/pricing-country-utils.service';
import { GetPricingCountryAction } from '../../../store/pricing-country.actions';
import { PricingCountryState } from '../../../store/pricing-country.state';
import {
  CountryBundlePrice,
  CountryBundleRow,
  CountryPriceType,
  PricingCountry,
} from '../../../types/pricing.types';
import { DeleteCountryPriceDialogComponent } from '../delete-country-price-dialog/delete-country-price-dialog.component';
import { CountryBulkPriceDialogComponent } from '../country-bulk-price-dialog/country-bulk-price-dialog.component';
import { CountryBundlePriceFormComponent } from '../country-bundle-price-form/country-bundle-price-form.component';
import { CountryPriceHistoryDialogComponent } from '../country-price-history-dialog/country-price-history-dialog.component';
import { EditCountryPriceDialogComponent } from '../edit-country-price-dialog/edit-country-price-dialog.component';

@Component({
  selector: 'app-pricing-country-dialog',
  imports: [
    DataTableComponent,
    CountryBundlePriceFormComponent,
    UserDisplayComponent,
    TranslatePipe,
    NgbTooltipModule,
    RequirePermissionDirective,
    DatePipe,
    DecimalPipe,
    NgTemplateOutlet,
  ],
  templateUrl: './pricing-country-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingCountryDialogComponent implements OnInit {
  public countryId = signal<string>(null);

  protected modalRef = inject(NgbActiveModal);
  protected coreUtilsService = inject(CoreUtilsService);
  private store = inject(Store);
  private ngbModal = inject(NgbModal);
  private pricingCountryUtilsService = inject(PricingCountryUtilsService);

  protected pricingCountry: Signal<PricingCountry> = computed(() =>
    this.store.selectSignal(PricingCountryState.getPricingCountryById)()(
      this.countryId()
    )
  );

  private bundlesList: Signal<Bundle[]> = computed(() =>
    this.store.selectSignal(ProductsListState.getBundlesList)()
  );
  private countries: Signal<Country[]> = this.store.selectSignal(
    CountriesState.getCountries
  );

  /**
   * One row per bundle: the bundles assigned to the country (Configuration >
   * Countries, matched by name) plus any bundle that already has a price,
   * each with its current WSP and EUP (or "missing").
   */
  protected rows: Signal<CountryBundleRow[]> = computed(() =>
    this.toBundleRows(this.pricingCountry())
  );
  private rows$ = toObservable(this.rows);

  protected bundleDataTableConfiguration: DataTableConfiguration;

  private wspColumnTemplate = viewChild<TemplateRef<any>>('wspColumnTemplate');
  private eupColumnTemplate = viewChild<TemplateRef<any>>('eupColumnTemplate');
  private bundleNameColumnTemplate = viewChild<TemplateRef<any>>(
    'bundleNameColumnTemplate'
  );
  private generatedByColumnTemplate = viewChild<TemplateRef<any>>(
    'generatedByColumnTemplate'
  );

  constructor() {
    this.store.dispatch(new GetCountriesAction());

    effect(() => {
      if (this.countryId()) {
        this.store.dispatch(new GetPricingCountryAction(this.countryId()));
      }
    });
  }

  ngOnInit(): void {
    this.bundleDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'pricing.country.details.actions.editPrice',
          icon: 'fa-light fa-pen',
          type: DataTableActionType.BUTTON,
          permission: 'pricing.country.actions',
          // like partners: only shown while a price is still missing
          invisible: (row: CountryBundleRow) =>
            !!row.wsp?.pivot?.price && !!row.eup?.pivot?.price,
          action: this.editPrices.bind(this),
        },
        {
          name: 'pricing.country.details.actions.priceHistory',
          icon: 'fa-light fa-list',
          type: DataTableActionType.BUTTON,
          disabled: (row: CountryBundleRow) => !row.wsp && !row.eup,
          action: this.openPriceHistory.bind(this),
        },
        {
          name: 'pricing.country.details.actions.deleteLatestPrice',
          icon: 'fa-light fa-xmark',
          type: DataTableActionType.BUTTON,
          permission: 'pricing.country.actions',
          disabled: (row: CountryBundleRow) =>
            !DeleteCountryPriceDialogComponent.isDeletable(row.wsp) &&
            !DeleteCountryPriceDialogComponent.isDeletable(row.eup),
          action: this.deletePrice.bind(this),
        },
      ],
      columns: [
        {
          label: 'pricing.country.details.columns.bundle',
          propertyTemplate: this.bundleNameColumnTemplate(),
        },
        {
          label: 'pricing.country.details.columns.wsp',
          propertyTemplate: this.wspColumnTemplate(),
        },
        {
          label: 'pricing.country.details.columns.eup',
          propertyTemplate: this.eupColumnTemplate(),
        },
        {
          label: 'pricing.country.details.columns.generatedBy',
          propertyTemplate: this.generatedByColumnTemplate(),
        },
      ],
      rows$: this.rows$,
    };
  }

  protected openCurrencyDialog(event: MouseEvent) {
    event.preventDefault();

    this.pricingCountryUtilsService.openEditCurrencyDialog(this.countryId());
  }

  protected openBulkPriceDialog(event: MouseEvent) {
    event.preventDefault();

    const modalRef = this.ngbModal.open(CountryBulkPriceDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.countryId.set(this.countryId());
  }

  protected getBundleDisplayName(bundle: CountryBundlePrice): string {
    return bundle?.internalName ?? bundle?.name ?? '';
  }

  /** The row's most recently generated price, for the "generated by" cell. */
  protected getLatestPrice(row: CountryBundleRow): CountryBundlePrice | null {
    const prices = [row.wsp, row.eup].filter((price) => !!price);

    if (!prices.length) {
      return null;
    }

    return prices.sort(
      (a, b) =>
        moment(b.pivot.validFrom).valueOf() - moment(a.pivot.validFrom).valueOf()
    )[0];
  }

  private toBundleRows(pricingCountry: PricingCountry): CountryBundleRow[] {
    if (!pricingCountry) {
      return [];
    }

    const countryName = pricingCountry.name?.toLowerCase();
    const configuredCountry = this.countries()?.find(
      (country) => country.name?.toLowerCase() === countryName
    );
    const rowsById = new Map<string, CountryBundleRow>();

    (configuredCountry?.bundleIds ?? []).forEach((bundleId) => {
      const bundle = this.bundlesList()?.find((item) => item.id === bundleId);

      rowsById.set(bundleId, {
        id: bundleId,
        name: bundle?.internalName ?? bundle?.name ?? bundleId,
        wsp: null,
        eup: null,
      });
    });

    (pricingCountry.pricelist ?? []).forEach((bundle) => {
      const row = rowsById.get(bundle.id) ?? {
        id: bundle.id,
        name: this.getBundleDisplayName(bundle),
        wsp: null,
        eup: null,
      };

      if (bundle.pivot?.type === CountryPriceType.WSP) {
        row.wsp = bundle;
      } else if (bundle.pivot?.type === CountryPriceType.EUP) {
        row.eup = bundle;
      }

      rowsById.set(bundle.id, row);
    });

    return [...rowsById.values()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  private editPrices(e: Event, row: CountryBundleRow) {
    const modalRef = this.ngbModal.open(EditCountryPriceDialogComponent, {
      size: 'md',
      centered: true,
    });

    modalRef.componentInstance.countryId.set(this.countryId());
    modalRef.componentInstance.row.set(row);
    modalRef.componentInstance.currency.set(this.pricingCountry()?.currency);
  }

  private openPriceHistory(e: Event, row: CountryBundleRow) {
    const modalRef = this.ngbModal.open(CountryPriceHistoryDialogComponent, {
      size: 'lg',
      centered: true,
    });

    modalRef.componentInstance.countryId.set(this.countryId());
    modalRef.componentInstance.row.set(row);
  }

  private deletePrice(e: Event, row: CountryBundleRow) {
    const modalRef = this.ngbModal.open(DeleteCountryPriceDialogComponent, {
      size: 'md',
      centered: true,
    });

    modalRef.componentInstance.countryId.set(this.countryId());
    modalRef.componentInstance.row.set(row);
    modalRef.componentInstance.currency.set(this.pricingCountry()?.currency);
  }
}
